import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { SimpleFormProvider } from "@/form";
import PhoneVerificationField from "@/form/fields/PhoneVerificationField/PhoneVerificationField";
import { cn } from "@/lib/utils";

const phoneVerificationSchema = z.object({
  verificationCode: z.string(),
});

type PhoneVerificationFormData = z.infer<typeof phoneVerificationSchema>;

const PhoneVerificationForm = ({
  onChange,
  onFinalChange,
  disabled,
}: {
  onChange?: (code: string) => void;
  onFinalChange?: (code: string) => void;
  disabled?: boolean;
}): React.ReactElement => {
  const { watch } = useFormContext<PhoneVerificationFormData>();
  const verificationCode = watch("verificationCode") || "";

  useEffect(() => {
    if (!disabled) {
      onChange?.(verificationCode);
    }

    if (verificationCode.length === 6 && !disabled) {
      onFinalChange?.(verificationCode);
    }
  }, [verificationCode, onChange, onFinalChange, disabled]);

  return (
    <PhoneVerificationField
      label="Verification Code"
      name="verificationCode"
      disabled={disabled}
      hideLabel={true}
      hideValidation={true}
    />
  );
};

export type PhoneVerificationCodeProps = {
  readonly phoneNumber?: string;
  readonly error?: boolean;
  readonly smsSendError?: boolean;
  readonly disabled?: boolean;
  readonly isVerifying?: boolean;
  readonly onResend?: () => void;
  readonly onBackToForm?: () => void;
  readonly onChange?: (code: string) => void;
  readonly onFinalChange?: (code: string) => void;
};

export default function PhoneVerificationCode({
  phoneNumber,
  error = false,
  smsSendError = false,
  disabled = false,
  isVerifying = false,
  onResend,
  onBackToForm,
  onChange,
  onFinalChange,
}: PhoneVerificationCodeProps): React.ReactElement {
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const handleResend = () => {
    if (onResend && !isResendDisabled) {
      setIsResendDisabled(true);
      onResend();

      setTimeout(() => {
        setIsResendDisabled(false);
      }, 5000);
    }
  };

  const getMaskedPhoneNumber = () => {
    if (!phoneNumber) return "********";
    const lastFour = phoneNumber.slice(-4);
    return `****${lastFour}`;
  };

  const handleSubmit = async () => {
    return Promise.resolve();
  };

  if (smsSendError) {
    return (
      <div
        className={cn(
          "flex w-full max-w-[330px] flex-col items-center gap-9",
          "PhoneVerificationCode"
        )}
        data-testid="PhoneVerificationCode"
      >
        <div className="flex w-full flex-col items-start gap-4">
          <h2 className="font-['STIX_Two_Text'] text-[24px] italic leading-[32px] text-[#091f24]">
            Unable to send verification code. Please try again
          </h2>
          <p className="font-['DM_Sans'] text-[14px] leading-[18px] text-[#6b7070]">
            {getMaskedPhoneNumber()}
          </p>
        </div>

        {onBackToForm && (
          <button
            type="button"
            onClick={onBackToForm}
            className={cn(
              "font-['DM_Sans'] text-[14px] font-medium text-[#c7b59e]",
              "transition-colors duration-200",
              "hover:text-[#c7b59e]/80 active:text-[#c7b59e]/60"
            )}
          >
            Back to form
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "PhoneVerificationCode flex w-full max-w-[330px] flex-col items-center gap-9"
      )}
      data-testid="PhoneVerificationCode"
    >
      <div className="flex w-full flex-col items-start gap-4">
        <h2 className="font-['STIX_Two_Text'] text-[24px] italic leading-[32px] text-[#091f24]">
          Enter the code we just sent to your phone.
        </h2>
        <p className="font-['DM_Sans'] text-[14px] leading-[18px] text-[#6b7070]">
          Code sent to your phone {getMaskedPhoneNumber()}
        </p>
      </div>

      <SimpleFormProvider
        schema={phoneVerificationSchema}
        defaultValues={{ verificationCode: "" }}
        onSubmit={handleSubmit}
        hideLoading={true}
        autoSubmit={false}
      >
        <PhoneVerificationForm
          onChange={onChange}
          onFinalChange={onFinalChange}
          disabled={disabled}
        />
      </SimpleFormProvider>

      {error && !disabled && !isVerifying && (
        <span className="block px-4 pt-1 text-body3 text-red">
          Failed to verify
        </span>
      )}

      {onResend && (
        <button
          type="button"
          onClick={handleResend}
          disabled={isResendDisabled || disabled}
          className={cn(
            "font-['DM_Sans'] text-[14px] font-medium text-[#c7b59e]",
            "transition-colors duration-200",
            isResendDisabled || disabled
              ? "cursor-not-allowed opacity-50"
              : "hover:text-[#c7b59e]/80 active:text-[#c7b59e]/60"
          )}
        >
          Resend code?
        </button>
      )}
    </div>
  );
}
